import {
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Icon,
  IconButton,
  IconProps,
  Row,
  Text,
} from '@wyz/ui';
import { useController, useFormContext } from 'react-hook-form';
import { Trans, useI18n } from '~/common/context';
import { FEATURES, GROUPS } from '~/common/helpers';
import { useAccessControl } from '~/common/hooks';
import { UserHelpers } from '~/connect/helpers';
import { GroupResponse, IUserForm } from '~/connect/types';
import { UserFormProps } from '~/connect/components';

// Display order of the "administration" column — any group not listed goes last.
const ADMINISTRATION_GROUPS_ORDER: string[] = [
  GROUPS.GROUP_PLATFORM_ADMIN,
  GROUPS.GROUP_CUSTOMER_MANAGER,
  GROUPS.GROUP_CONSULTANT,
  GROUPS.GROUP_REDISTRIBUTION_PRICING_POLICY,
  GROUPS.GROUP_PURCHASE_ADMIN,
  GROUPS.GROUP_QUOTE_SETTINGS,
];

function administrationRank(codename: string) {
  const index = ADMINISTRATION_GROUPS_ORDER.indexOf(codename);
  return index === -1 ? ADMINISTRATION_GROUPS_ORDER.length : index;
}

type GroupTypeProps = {
  groups: GroupResponse['authorizedGroups'];
  groupsField: string[];
  icon: IconProps['icon'];
  title: string;
  onChange: (groups: string[]) => void;
  disabled?: boolean;
  isReadOnly?: boolean;
};

function GroupType({
  groupsField,
  groups,
  icon,
  title,
  onChange,
  disabled = false,
  isReadOnly = false,
}: GroupTypeProps) {
  const selectedGroups = groupsField.filter((selected) =>
    groups.some((group) => group.codename === selected),
  );

  return (
    <div>
      <Card style={{ minHeight: 260 }}>
        <Card.Body>
          <Flex className='gap-1 mb-3'>
            <Icon icon={icon} size='sm' className='text-tertiary' />
            <Text heading='normal' weight='bold' color='tertiary'>
              {title}
            </Text>
          </Flex>
          {groups.map((group) => {
            const isChecked = groupsField.includes(group.codename);
            return (
              <Checkbox
                disabled={disabled || isReadOnly}
                key={group.codename}
                id={group.codename}
                name={group.codename}
                value={group.codename}
                label={
                  <Trans
                    i18nKey={UserHelpers.USER_GROUPS_LOOKUP[group.codename]}
                  />
                }
                data-cy={group.codename}
                checked={isChecked}
                onChange={() => {
                  onChange(
                    isChecked
                      ? groupsField.filter((value) => group.codename !== value)
                      : [...(groupsField ?? []), group.codename],
                  );
                }}
              />
            );
          })}
        </Card.Body>
      </Card>
      <Card className='my-3'>
        {!isReadOnly && (
          <Card.Body>
            <Flex justifyContent='end' className='gap-2'>
              <Button
                id='unselect-all'
                type='button'
                variant='primary'
                size='sm'
                onClick={() =>
                  onChange(
                    groupsField.filter(
                      (selected) =>
                        !groups.some((group) => group.codename === selected),
                    ),
                  )
                }
                disabled={selectedGroups.length === 0 || disabled}
              >
                <Trans i18nKey='app.user.create.form.groups_section.unselect_all' />
              </Button>
              <Button
                id='select-all'
                type='button'
                variant='success'
                size='sm'
                onClick={() =>
                  onChange(
                    Array.from(
                      new Set([
                        ...groupsField,
                        ...groups.map((group) => group.codename),
                      ]),
                    ),
                  )
                }
                disabled={selectedGroups.length === groups.length || disabled}
              >
                <Trans i18nKey='app.user.create.form.groups_section.select_all' />
              </Button>
            </Flex>
          </Card.Body>
        )}
      </Card>
    </div>
  );
}

type GroupSectionProps = {
  groups: GroupResponse['authorizedGroups'];
  onSelect: (groups: GroupResponse['authorizedGroups']) => void;
  mode: UserFormProps['mode'];
};

function GroupSection({ groups, onSelect, mode }: GroupSectionProps) {
  const { t } = useI18n();
  const methods = useFormContext<IUserForm>();
  const { field } = useController({
    control: methods.control,
    name: 'groups',
    shouldUnregister: true,
  });
  const administrationGroups = groups
    .filter((group) => group.type === 'administration')
    .sort(
      (a, b) => administrationRank(a.codename) - administrationRank(b.codename),
    );

  function onChange(newGroups: string[]) {
    if (!newGroups.includes(GROUPS.GROUP_CONTAINER_SUPPLIER)) {
      methods.setValue('supplierHqId', null);
    }
    const selectedGroups = groups.filter((group) =>
      newGroups.includes(group.codename),
    );

    field.onChange(newGroups);

    onSelect(selectedGroups);
  }

  const { isEnabledFeatures } = useAccessControl();
  const isRedistributionEnabled = isEnabledFeatures([FEATURES.REDISTRIBUTION]);
  const isQuoteEnabled = isEnabledFeatures([FEATURES.QUOTE]);
  const isContainerEnabled = isEnabledFeatures([FEATURES.CONTAINER]);
  const isReadOnly = mode === 'DETAIL';

  return (
    <>
      <Flex justifyContent='between' alignItems='center' className='my-3'>
        <Text heading='normal' weight='bold' color='tertiary'>
          <Trans i18nKey='app.user.create.form.groups_section.title' />
        </Text>
        {!isReadOnly && (
          <IconButton
            id='remove-all-roles'
            variant='tertiary-blue'
            icon='trash'
            iconSize='xs'
            disabled={field.value.length === 0}
            onClick={() => onChange([])}
          >
            <Text heading='xs' weight='semi-bold' color='tertiary'>
              <Trans i18nKey='app.user.create.form.groups_section.remove_selections' />
            </Text>
          </IconButton>
        )}
      </Flex>
      <Row>
        <Col xs={12} md={6}>
          <GroupType
            isReadOnly={isReadOnly}
            groupsField={field.value}
            onChange={onChange}
            groups={groups.filter((group) => group.type === 'purchase')}
            icon='money-euro-box'
            title={t('app.user.create.form.groups_section.categoy.purchase')}
          />
        </Col>
        {isQuoteEnabled && (
          <Col xs={12} md={6}>
            <GroupType
              isReadOnly={isReadOnly}
              groupsField={field.value}
              onChange={onChange}
              groups={groups.filter((group) => group.type === 'quote')}
              icon='quote'
              title={t('app.user.create.form.groups_section.categoy.quote')}
            />
          </Col>
        )}
        {isRedistributionEnabled && (
          <Col xs={12} md={6}>
            <GroupType
              isReadOnly={isReadOnly}
              groupsField={field.value}
              onChange={onChange}
              groups={groups.filter((group) => group.type === 'seller')}
              icon='shopping-cart'
              title={t('app.user.create.form.groups_section.categoy.seller')}
            />
          </Col>
        )}
        <Col xs={12} md={6}>
          <GroupType
            isReadOnly={isReadOnly}
            groupsField={field.value}
            onChange={onChange}
            groups={administrationGroups}
            icon='settings'
            title={t(
              'app.user.create.form.groups_section.categoy.administration',
            )}
          />
        </Col>
        {isContainerEnabled && (
          <Col xs={12} md={6}>
            <GroupType
              isReadOnly={isReadOnly}
              groupsField={field.value}
              onChange={onChange}
              groups={groups.filter((group) => group.type === 'container')}
              icon='shopping-cart'
              title={t('app.user.create.form.groups_section.categoy.bulk')}
            />
          </Col>
        )}
      </Row>
    </>
  );
}

export default GroupSection;
